// Phase 2.2 D-211 — Banner crítico não-operacional para web-comite.
//
// Renderizado no topo de páginas que dependem de composição operacional
// (TriagemPage). Faz polling GET /api/comite/composicao a cada 30s
// (paridade Plan 04 web-admin Dashboard polling — OD-4: SSE/WS é v2).
//
// operacional === true → renderiza null (sem ruído visual).
// operacional === false → renderiza alert com missing roles list.
//
// NÃO é guard absoluto — Backend tem 503 enforcement (Task 1); este banner
// é UX-defense que sinaliza ANTES do submit. Race-condition (admin
// removendo membro entre fetch e submit) cai no 503 handler de TriagemPage.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/auth-client';

interface ComposicaoStatus {
  readonly operacional: boolean;
  readonly missing: ReadonlyArray<string>;
}

const POLL_INTERVAL_MS = 30_000; // 30s — OD-4 + RESEARCH §Notification Fan-out

export function ComiteNonOperationalBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ComposicaoStatus | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async (): Promise<void> => {
      try {
        const resp = await apiFetch('/api/comite/composicao');
        if (!resp.ok) return; // 401/403 — banner sumiço silent (não bloqueia outras pages)
        const body = (await resp.json()) as Partial<ComposicaoStatus>;
        // Defensive parse: response sem shape esperado (e.g. mocks em testes
        // que não simulam o composicao endpoint) → banner sumiço silent.
        if (typeof body.operacional !== 'boolean' || !Array.isArray(body.missing)) return;
        if (!cancelled) setStatus({ operacional: body.operacional, missing: body.missing });
      } catch {
        // Network error — banner sumiço silent
      }
    };

    void fetchStatus();
    const interval = setInterval(() => void fetchStatus(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (!status || status.operacional) return null;

  return (
    <div className="alert alert-error" role="alert" data-testid="comite-non-operational-banner">
      <strong>{t('comite.non_operational.title')}</strong>
      <p>{t('comite.non_operational.detail', { missing: status.missing.join(', ') })}</p>
    </div>
  );
}
